import React from 'react';
import { cn, Text, PillBadge, Button } from '@kognitos/lattice';
import { cva, type VariantProps } from 'class-variance-authority';

const inputsTriggerVariants = cva(
  'backdrop-blur-lg border border-border rounded-xl shadow-3xl dark:shadow-xl',
  {
    variants: {
      variant: {
        default: 'p-1 flex items-center gap-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface IInputsTriggerButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof inputsTriggerVariants> {
  /** Number of inputs to display in badge */
  count?: number;
  /** Label text for default variant */
  label?: string;
}

/**
 * InputsTriggerButton Component
 *
 * A floating trigger button for the automation inputs sheet with glassmorphic design.
 * Shows icon, label, and count badge.
 *
 * @example
 * ```tsx
 * <InputsTriggerButton
 *   count={5}
 *   label="Inputs"
 *   onClick={() => setIsOpen(true)}
 * />
 * ```
 */
export const InputsTriggerButton = React.memo(
  ({
    variant = 'default',
    count,
    label = 'Inputs',
    className,
    onClick,
    ...props
  }: IInputsTriggerButtonProps) => {
    return (
      <Button
        variant="ghost"
        className={cn(inputsTriggerVariants({ variant }), className)}
        onClick={onClick}
        {...props}
      >
        {/* Label with count badge */}
        {count !== undefined ? (
          <PillBadge
            count={count > 0 ? count : undefined}
            className="bg-transparent border-none shadow-none px-2"
          >
            <Text
              level="small"
              weight="medium"
              className="text-muted-foreground"
            >
              {label}
            </Text>
          </PillBadge>
        ) : (
          <Text level="small" weight="medium">
            {label}
          </Text>
        )}
      </Button>
    );
  },
);

InputsTriggerButton.displayName = 'InputsTriggerButton';
